import { PeopleModel } from '../../search-api.model';
import {
  ProximitySearchResults,
  UserProfileSummary,
} from '../../../user/user-api.model';
import {
  Component,
  OnInit,
  Inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { SearchService } from '@app/search/services/search.service';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { NUM_TILE_RESULTS_PER_PAGE } from '@app/shared/search.token';
import { SearchSuggestionSource } from '@app/search/search-api.model';
import { SearchOrigin } from '@app/search/services/search-tracker.service';
import { AuthService } from '@app/shared/services/auth.service';
import { SearchFilterId } from '@app/search/services';
import { SearchFlagsService } from '@app/search/services/search-flags.service';

// The child user card component expects a compound type for user profiles, so...
export interface UserCardPeopleModel extends PeopleModel {
  profiles: (ProximitySearchResults & UserProfileSummary)[];
}

export interface SearchProperties {
  term: string;
  orgId: number;
  numResultsToSkip: number;
  filters: any[];
}

@Component({
  selector: 'dgx-search-people-view',
  templateUrl: './search-people-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchPeopleViewComponent implements OnInit {
  public filterIds: string[] = [];
  public filterIdsToIgnore: SearchFilterId[] = [SearchFilterId.Type];
  public hasError = false;
  public searchOrigin = SearchOrigin.people;
  public suggestionSource = SearchSuggestionSource.AutoSuggestUsers;

  public data$: Observable<UserCardPeopleModel>;
  public search$ = new Subject<SearchProperties>();

  constructor(
    private searchService: SearchService,
    private authService: AuthService,
    private searchFlagsService: SearchFlagsService,
    @Inject(NUM_TILE_RESULTS_PER_PAGE)
    private readonly numResultsPerPage: number
  ) {}

  public get useFilterSidebar(): boolean {
    return this.searchFlagsService.showSearchLayoutRedesign;
  }

  private get showUserSearchFilterByLocation(): boolean {
    return this.authService.authUser?.showUserSearchFilterByLocation;
  }

  private get showUserSearchFilterByJobRole(): boolean {
    return this.authService.authUser?.showUserSearchFilterByJobRole;
  }

  private get showUserSearchFiltersMentoring(): boolean {
    return this.authService.isOrgUser;
  }

  public get showSearchTabs(): boolean {
    return this.searchFlagsService.showSearchLayoutRedesign;
  }

  public ngOnInit(): void {
    this.data$ = this.search$.pipe(
      switchMap(this.search.bind(this)),
      shareReplay()
    );

    if (this.showUserSearchFilterByLocation) {
      this.filterIds.push('Location');
    } else {
      this.filterIdsToIgnore.push(SearchFilterId.Location);
    }

    if (this.showUserSearchFilterByJobRole) {
      this.filterIds.push('JobRole');
    } else {
      this.filterIdsToIgnore.push(SearchFilterId.JobRole);
    }

    if (this.showUserSearchFiltersMentoring) {
      this.filterIds.push('Mentors');
    } else {
      this.filterIdsToIgnore.push(SearchFilterId.Mentors);
    }
  }

  private search({ term, filters, numResultsToSkip, orgId }: SearchProperties) {
    return this.searchService
      .peopleSearchFull(
        term,
        orgId,
        this.numResultsPerPage,
        numResultsToSkip,
        filters
      )
      .pipe(
        // We don't need the Type filter
        map((data) => {
          const filters = data.filters.filter(
            ({ model }) => model.id !== SearchFilterId.Type
          );
          return {
            ...data,
            filters,
          };
        }),
        catchError(() => {
          this.hasError = true;
          // Return EMPTY here rather than of(null); EMPTY
          // is a "never" stream, perfect for when we want
          // to end the stream by returning an Observable
          // with no content.
          return EMPTY;
        })
      );
  }
}
